import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Text from 'components/shared/Text';
import Table from 'components/shared/Table';
import { formatCreatedAt } from 'helpers/dates';
import { formatAmount, formatAsset } from '@nbx/frontend-helpers/format';
import { orderDataToStatus, getFillsQuantitySum } from 'helpers/orders';
import styles from './styles.module.scss';

const microapps = window.MICROFRONTENDS_CONFIG;

const columnWidths = [1, 2, 2, 2, 1.5, 3.5];
const endAlignIndex = 5;

function OrdersClosed(props) {
  const { t } = useTranslation();

  const headers = [
    t('common_side'),
    t('common_amount'),
    t('common_price'),
    t('common_filled'),
    t('common_status'),
    t('common_date')
  ];

  const renderItem = useCallback(
    (order, index) =>
      ({
        0: (
          <Text
            styles={[
              styles.cellText,
              { [styles.buy]: order.side === 'BUY', [styles.sell]: order.side === 'SELL' }
            ]}>
            {order.side === 'BUY' ? t('common_buy') : t('common_sell')}
          </Text>
        ),
        1: (
          <Text monospaced styles={[styles.cellText]}>
            {formatAsset(order.quantity, props.market.baseAsset, props.assets)}
          </Text>
        ),
        2: (
          <Text monospaced styles={[styles.cellText]}>
            {order.execution.type === 'MARKET'
              ? t('common_market')
              : formatAmount(order.execution.price, props.market.minTickLength)}
          </Text>
        ),
        3: (
          <Text monospaced styles={[styles.cellText]}>
            {formatAsset(getFillsQuantitySum(order), props.market.baseAsset, props.assets)}
          </Text>
        ),
        4: (
          <Text monospaced styles={[styles.cellText]}>
            {orderDataToStatus(order)}
          </Text>
        ),
        5: (
          <Text monospaced styles={[styles.cellText, styles.createdAtText]}>
            {formatCreatedAt(order.events.createdAt, true)}
          </Text>
        )
      }[index]),
    [t, props.assets, props.market.baseAsset, props.market.minTickLength]
  );
  const renderLink = useCallback(order => `${microapps.routes.orders.path}/${order.id}`, []);

  return (
    <Table
      columnWidths={columnWidths}
      data={props.closedOrders.data}
      keyField="id"
      hasNextPage={props.closedOrders.hasNextPage && !props.closedOrders.pausedForEmptyPages}
      pausedForEmptyPages={props.closedOrders.pausedForEmptyPages}
      error={props.closedOrders.error}
      isLoading={props.closedOrders.isLoading}
      fetchNextPage={props.closedOrders.fetchNextPage}
      fetchAllPages={props.closedOrders.fetchAllPages}
      isLoadingNextPage={props.closedOrders.loadingNextPage}
      isLoadingAllPages={props.closedOrders.loadingAllPages}
      setRefreshEndIndex={props.closedOrders.setRefreshEndIndex}
      emptyTitle={t('market_you_dont_have_closed')}
      emptyMessage={t('market_once_filled')}
      emptyShowBlob={false}
      endAlignIndex={endAlignIndex}
      headers={headers}
      renderItem={renderItem}
      renderLink={renderLink}
      borders={false}
      rowHeight={40}
      tableStyles={[styles.table]}
      headerStyles={[styles.headers]}
      hasMaxHeight
    />
  );
}

OrdersClosed.propTypes = {
  closedOrders: PropTypes.object.isRequired,
  market: PropTypes.object.isRequired,
  assets: PropTypes.object.isRequired
};

export default OrdersClosed;
